// AIDEV-NOTE: Hook generico para buscar relatorios via TanStack Query
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../hooks/useAuth';
import type {
  ReportFilter,
  ReportResponse,
  VendasReportRow,
  CRMReportRow,
  VisitasReportRow
} from '@azoupvendas/shared';

type ReportRowMap = {
  vendas: VendasReportRow;
  crm: CRMReportRow;
  visitas: VisitasReportRow;
};

async function fetchReport<T extends keyof ReportRowMap>(
  tipo: T,
  filter: ReportFilter
): Promise<ReportResponse<ReportRowMap[T]>> {
  const params = new URLSearchParams();
  if (filter.periodo) params.set('periodo', filter.periodo);
  if (filter.vendedorId) params.set('vendedorId', String(filter.vendedorId));
  if (filter.limit) params.set('limit', String(filter.limit));
  if (filter.offset) params.set('offset', String(filter.offset));

  const res = await fetch(`/api/v1/reports/${tipo}?${params.toString()}`, {
    credentials: 'include'
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Erro ao buscar relatorio de ${tipo}`);
  }

  const { data } = await res.json();
  return data;
}

export function useReport<T extends keyof ReportRowMap>(tipo: T, filter: ReportFilter) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['reports', tipo, filter, user?.client_id],
    queryFn: () => fetchReport(tipo, filter),
    staleTime: 5 * 60 * 1000,
    enabled: !!user
  });
}
